import {
  formattedText,
  idOf,
  isGuid,
  isoOf,
} from '@omni/utility/dynamics-row.util';
import { DynamicsClientService } from '@omni/data-services/dynamics-client/dynamics-client.service';
import { LINE_SUMMARY } from './order-list';
import { promisePool } from '@omni/utility/promise-pool';

type OrderRow = Record<string, unknown>;
type Fetcher = Pick<DynamicsClientService, 'executeFetchQuery'>;

/** Orders summarized per query: an aggregate reads at most 50,000 lines. */
const SUMMARY_BATCH = 50;
const SUMMARY_CONCURRENCY = 3;

/**
 * Gives each order without one a summary of its lines (`LINE_SUMMARY`): its first product, how many
 * it orders and in how many deliveries, and the first of those. Two aggregates per batch, never the
 * lines themselves. A batch the server refuses (too many lines) is halved down to single orders; an
 * order that still fails is marked so a later sync doesn't ask again until its lines change.
 */
export async function summarizeOrderLines(
  dynamics: Fetcher,
  orders: readonly OrderRow[],
): Promise<void> {
  const pending = orders.filter(
    (order) => !(LINE_SUMMARY.products in order) && !order[LINE_SUMMARY.failed],
  );
  const batches: OrderRow[][] = [];
  for (let at = 0; at < pending.length; at += SUMMARY_BATCH)
    batches.push(pending.slice(at, at + SUMMARY_BATCH));
  await promisePool(
    batches.map((batch) => async () => summarizeBatch(dynamics, batch)),
    SUMMARY_CONCURRENCY,
  );
}

async function summarizeBatch(
  dynamics: Fetcher,
  batch: OrderRow[],
): Promise<void> {
  const ids = batch.map((order) => idOf(order.salesorderid));
  try {
    const [products, deliveries] = await Promise.all([
      dynamics.executeFetchQuery('salesorderdetails', lineProductsFetch(ids)),
      dynamics.executeFetchQuery('salesorderdetails', lineDeliveriesFetch(ids)),
    ]);
    applyLineSummaries(batch, products ?? [], deliveries ?? []);
  } catch {
    if (batch.length === 1) {
      batch[0][LINE_SUMMARY.failed] = true;
      return;
    }
    const half = Math.ceil(batch.length / 2);
    await summarizeBatch(dynamics, batch.slice(0, half));
    await summarizeBatch(dynamics, batch.slice(half));
  }
}

/** The orders' bought lines with a quantity; free goods are rewards, not what was ordered. */
function boughtLines(ids: readonly string[]): string {
  const values = ids
    .filter(isGuid)
    .map((id) => `<value>${id}</value>`)
    .join('');
  return (
    `<filter type="and"><condition attribute="salesorderid" operator="in">${values}</condition>` +
    `<condition attribute="quantity" operator="gt" value="0" />` +
    `<filter type="or"><condition attribute="indskr_isfreegood" operator="null" />` +
    `<condition attribute="indskr_isfreegood" operator="eq" value="0" /></filter></filter>`
  );
}

/** Each order's distinct products, with the first line each is on. */
export function lineProductsFetch(ids: readonly string[]): string {
  return (
    `<fetch aggregate="true"><entity name="salesorderdetail">` +
    `<attribute name="salesorderid" alias="order" groupby="true" />` +
    `<attribute name="productid" alias="product" groupby="true" />` +
    `<attribute name="lineitemnumber" alias="line" aggregate="min" />` +
    `${boughtLines(ids)}</entity></fetch>`
  );
}

/**
 * How many of each order's deliveries hold a bought line, and the first of them: the order's own
 * first delivery may be an empty one (the primary, or a date the rep picked).
 */
export function lineDeliveriesFetch(ids: readonly string[]): string {
  return (
    `<fetch aggregate="true"><entity name="salesorderdetail">` +
    `<attribute name="salesorderid" alias="order" groupby="true" />` +
    `<attribute name="indskr_orderdelivery" alias="deliveries" aggregate="countcolumn" distinct="true" />` +
    `<link-entity name="indskr_orderdelivery" from="indskr_orderdeliveryid" to="indskr_orderdelivery" link-type="outer" alias="drop">` +
    `<attribute name="indskr_deliverydate" alias="firstdelivery" aggregate="min" /></link-entity>` +
    `${boughtLines(ids)}</entity></fetch>`
  );
}

/** Writes `LINE_SUMMARY` onto each order from the two aggregates' rows. */
export function applyLineSummaries(
  orders: readonly OrderRow[],
  products: readonly OrderRow[],
  deliveries: readonly OrderRow[],
): void {
  const byOrder = new Map<string, OrderRow[]>();
  for (const row of products) {
    const id = idOf(row.order);
    byOrder.set(id, [...(byOrder.get(id) ?? []), row]);
  }
  const drops = new Map(deliveries.map((row) => [idOf(row.order), row]));
  for (const order of orders) {
    const id = idOf(order.salesorderid);
    const own = byOrder.get(id) ?? [];
    const first = own.reduce<OrderRow | null>(
      (best, row) =>
        !best || Number(row.line) < Number(best.line) ? row : best,
      null,
    );
    const drop = drops.get(id);
    order[LINE_SUMMARY.products] = own.length;
    order[LINE_SUMMARY.firstProduct] = first
      ? formattedText(first, 'product')
      : '';
    // A line saved before deliveries existed has none; the order still delivers it once.
    order[LINE_SUMMARY.deliveries] = own.length
      ? Math.max(1, Number(drop?.deliveries) || 0)
      : 0;
    order[LINE_SUMMARY.firstDelivery] =
      isoOf(drop?.firstdelivery).slice(0, 10) || null;
  }
}
